import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import Navbar from './components/Navbar';
import Footer from './components/Footer';
import LoadingScreen from './components/LoadingScreen';
import CartDrawer from './components/CartDrawer';
import Home from './pages/Home';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import AuthPage from './pages/AuthPage';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import Profile from './pages/Profile';
import Orders from './pages/Orders';
import OrderSuccess from './pages/OrderSuccess';
import AdminDashboard from './pages/admin/Dashboard';
import AdminAnalytics from './pages/admin/Analytics';
import AdminProducts from './pages/admin/Products';
import AdminOrders from './pages/admin/Orders';
import AdminReviews from './pages/admin/Reviews';
import AdminPayments from './pages/admin/Payments';
import AdminUsers from './pages/admin/Users';
import AdminSettings from './pages/admin/Settings';
import About from './pages/About';
import Contact from './pages/Contact';
import FAQs from './pages/FAQs';
import PolicyPage from './pages/PolicyPage';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import ProtectedRoute from './components/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop';
import RiderOrder from './pages/RiderOrder';
import { trackPageView, trackEvent, isAdminUser } from './utils/analytics';

const App = () => {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);

  const { lang } = useLanguage();
  const isFirstLoad = useRef(true);

  // Show loading screen on route change or language change
  useEffect(() => {
    setIsLoading(true);

    const delay = isFirstLoad.current ? 1500 : 400;

    const timer = setTimeout(() => {
      setIsLoading(false);
      isFirstLoad.current = false;
    }, delay);

    return () => clearTimeout(timer);
  }, [location.pathname, lang, location.search]);

  // Track page views on route transitions (strictly non-admin client pageviews)
  useEffect(() => {
    if (!location.pathname.startsWith('/admin') && !isAdminUser()) {
      trackPageView(location.pathname + location.search, document.title);
    }
  }, [location.pathname, location.search]);

  // Keep heartbeat active every 60s for accurate live customer metrics (excluding admins)
  useEffect(() => {
    if (location.pathname.startsWith('/admin') || isAdminUser()) return;

    const heartbeatTimer = setInterval(() => {
      if (document.visibilityState === 'visible' && !isAdminUser()) {
        trackEvent('heartbeat');
      }
    }, 60000);

    return () => clearInterval(heartbeatTimer);
  }, [location.pathname]);

  // Make cart drawer controls available globally
  useEffect(() => {
    window.openCartDrawer = () => setCartDrawerOpen(true);
    window.closeCartDrawer = () => setCartDrawerOpen(false);
  }, []);

  return (
    <div className="app">
      <LoadingScreen isLoading={isLoading} />
      <ScrollToTop />
      <Navbar />
      <CartDrawer isOpen={cartDrawerOpen} onClose={() => setCartDrawerOpen(false)} />

      <main className={`main-content${location.pathname === '/' ? ' home-active' : ''}${location.pathname.startsWith('/admin') ? ' admin-active' : ''}${location.pathname.startsWith('/products/') ? ' product-detail-active' : ''}`}>
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<Home />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/:id" element={<ProductDetail />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/login" element={<AuthPage />} />
          <Route path="/register" element={<AuthPage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faqs" element={<FAQs />} />
          <Route path="/policies/shipping-returns" element={<PolicyPage type="shipping-returns" />} />
          <Route path="/policies/terms" element={<PolicyPage type="terms" />} />
          <Route path="/policies/payment" element={<PolicyPage type="payment" />} />
          <Route path="/policies/privacy" element={<PolicyPage type="privacy" />} />
          <Route path="/policies/refund" element={<PolicyPage type="refund" />} />

          {/* Protected User Routes */}
          <Route
            path="/checkout"
            element={
              <ProtectedRoute>
                <Checkout />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders"
            element={
              <ProtectedRoute>
                <Orders />
              </ProtectedRoute>
            }
          />

          {/* Admin Protected Routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute adminOnly>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/analytics"
            element={
              <ProtectedRoute adminOnly>
                <AdminAnalytics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/products"
            element={
              <ProtectedRoute adminOnly>
                <AdminProducts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/orders"
            element={
              <ProtectedRoute adminOnly>
                <AdminOrders />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/reviews"
            element={
              <ProtectedRoute adminOnly>
                <AdminReviews />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/payments"
            element={
              <ProtectedRoute adminOnly>
                <AdminPayments />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute adminOnly>
                <AdminUsers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute adminOnly>
                <AdminSettings />
              </ProtectedRoute>
            }
          />
          <Route path="/order-success/:id" element={<OrderSuccess />} />
          <Route path="/rider/order/*" element={<RiderOrder />} />
        </Routes>
      </main>

      <Footer />
      <ToastContainer position="top-right" />
    </div>
  );
};

export default App;
